// Vegetation PAINTERS — the pure canvas-2D half of the game's trees, bushes
// and grass tufts, with no three.js in it. The game (trees.js, grass.js,
// shrubs.js) wraps each painted canvas in a THREE.CanvasTexture; the ops
// panel's Vegetation modal loads the SAME code as a bundle (vegpreview.js →
// GET api/vegpreview.js) and paints the previews with it. One painter, two
// consumers: a preview drawn by a copy would drift from what the headset
// draws, and the preview exists to show exactly that.
//
// Moved here verbatim from trees.js / grass.js (the pixels are identical —
// check the painters against git history before changing a single stroke).
// Nothing here may touch `document` or `THREE` at import time: the canvas
// is always handed in.

export const NVAR = 3;                         // silhouette variants per species atlas

export const FORM_CFG = {
  conifer:   { color: '#2c5231', w: 0.55, hScale: 1.0  },
  larch:     { color: '#5d8a3a', w: 0.45, hScale: 0.9  },
  broadleaf: { color: '#3f6a2e', w: 0.62, hScale: 1.0  },
  palm:      { color: '#3a7a32', w: 0.35, hScale: 1.0  },
  columnar:  { color: '#2a5a2a', w: 0.28, hScale: 1.0  },
  spreading: { color: '#4a6a2a', w: 0.85, hScale: 0.75 },
  umbrella:  { color: '#3a6a28', w: 0.72, hScale: 1.0  },
  birch:     { color: '#5a8a4a', w: 0.50, hScale: 0.95 },
};
const _DCFG = FORM_CFG.conifer;
export const HEXRE = /^#[0-9a-f]{6}$/i;

// ---- the vegetation LOOK (renderconfig look.veg / look.veg_sprite; APP_SURFACE.md
// "Light"): a sprite canvas holds display-ready colours, so before the frame's
// one curve each fragment is desaturated toward its luminance (sat, 1 = as
// drawn) and scaled (gain): rgb = mix(lum, rgb, sat) * gain, in linear. The
// painted sprites take look.veg; the OWNER's PNGs (species sprite, shrub
// sprite_leafy / sprite_needle) take region.vegetation.sprite_look, else
// look.veg_sprite. The GLSL is look.js setupLook (chunk veg_own_look_fragment,
// these luma weights); vegLookImage is its JS twin for the dashboard preview.
export const VEG_LUMA = [0.2126, 0.7152, 0.0722];
export const VEG_LOOK_GAIN = [0, 4];          // gain in (0, 4] — newregion.py VEG_LOOK_LIM in step
export const VEG_LOOK_SAT = [0, 1.5];         // sat in [0, 1.5]
export function vegLookOk(k, x) {
  const [lo, hi] = k === 'gain' ? VEG_LOOK_GAIN : VEG_LOOK_SAT;
  return typeof x === 'number' && Number.isFinite(x) && x <= hi && (k === 'gain' ? x > lo : x >= lo);
}
// the owner look in force: each of sprite_look's fields when valid, else the
// fallback's (renderconfig look.veg_sprite)
export function vegSpriteLook(sl, dflt) {
  const o = sl && typeof sl === 'object' ? sl : {};
  return {
    gain: vegLookOk('gain', o.gain) ? o.gain : dflt.gain,
    sat: vegLookOk('sat', o.sat) ? o.sat : dflt.sat,
  };
}
const _lin = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const _enc = (c) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);
// three's NeutralToneMapping (tonemapping_pars_fragment), exposure first
function _neutral(c, exposure) {
  for (let i = 0; i < 3; i++) c[i] *= exposure;
  const x = Math.min(c[0], c[1], c[2]);
  const off = x < 0.08 ? x - 6.25 * x * x : 0.04;
  for (let i = 0; i < 3; i++) c[i] -= off;
  const peak = Math.max(c[0], c[1], c[2]);
  if (peak < 0.76) return c;
  const d = 0.24, np = 1 - d * d / (peak + d - 0.76);
  const g = 1 - 1 / (0.15 * (peak - np) + 1);
  for (let i = 0; i < 3; i++) c[i] = c[i] * (np / peak) * (1 - g) + np * g;
  return c;
}
// the preview twin, in place on RGBA bytes (alpha untouched): sRGB → linear,
// the look, the Neutral curve (tone 'none' skips it; agx / aces are shown as
// Neutral), → sRGB. APPROXIMATION
// (stated in APP_VEGETATION.md): no shadow_tint lean (only linear luminance
// below look.shadow_lum, i.e. the darkest needles, leans 25 % toward the sky
// blue in the game), no fog, no mipmaps.
export function vegLookImage(px, look, tone = 'neutral', exposure = 1) {
  const lut = new Float32Array(256);
  for (let i = 0; i < 256; i++) lut[i] = _lin(i / 255);
  const c = [0, 0, 0];
  for (let k = 0; k < px.length; k += 4) {
    if (!px[k + 3]) continue;
    const r = lut[px[k]], g = lut[px[k + 1]], b = lut[px[k + 2]];
    const l = VEG_LUMA[0] * r + VEG_LUMA[1] * g + VEG_LUMA[2] * b;
    c[0] = (l + (r - l) * look.sat) * look.gain;
    c[1] = (l + (g - l) * look.sat) * look.gain;
    c[2] = (l + (b - l) * look.sat) * look.gain;
    if (tone !== 'none') _neutral(c, exposure);
    for (let i = 0; i < 3; i++) px[k + i] = Math.round(255 * _enc(Math.min(1, Math.max(0, c[i]))));
  }
  return px;
}

// the alpha cut each material applies (alphaTest, fwidth-sharpened in the
// shader — effectively a hard threshold). The dashboard preview applies the
// same numbers, so what it shows is what survives the test in the headset.
export const TREE_ALPHA = 0.45;
export const BUSH_ALPHA = 0.4;
export const TUFT_ALPHA = 0.38;
export const FLOWER_ALPHA = 0.4;

// deterministic tiny hash for painterly jitter (no Math.random — the sprite
// must be identical every build)
export function _h01(a, b) {
  let h = (Math.imul(a, 374761393) + Math.imul(b, 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// --------------------------------------------------------------- sprite atlas
// NVAR silhouette variants per species, side by side in one texture. The
// painting is FORM-TRUE: conifer/larch crowns are built from drooping branch
// strokes fringed with individual NEEDLE strokes (never leafy blobs), larch
// airier and lighter with more visible trunk; broadleaf is a lobed blob crown
// with leaf speckle on a forked trunk. All deterministic (_h01 jitter).
// Paints into the canvas it is handed (resized to S·NVAR × 2S) and returns
// 'builtin' | 'custom' | 'fallback' — how the form was drawn.
export function paintSpeciesCanvas(cv, form, colorHex, S, vegForms) {
  let status = 'builtin';
  const cr = parseInt(colorHex.slice(1, 3), 16);
  const cg = parseInt(colorHex.slice(3, 5), 16);
  const cb = parseInt(colorHex.slice(5, 7), 16);
  const H = S * 2;
  cv.width = S * NVAR; cv.height = H;
  const g = cv.getContext('2d');
  g.clearRect(0, 0, cv.width, H);
  const rgb = (k) => `rgb(${Math.min(255, cr * k) | 0},${Math.min(255, cg * k) | 0},${Math.min(255, cb * k) | 0})`;
  for (let v = 0; v < NVAR; v++) {
    g.save();
    g.translate(v * S, 0);
    const st = paintForm(form, g, S, H, rgb, v, vegForms);
    if (st !== 'builtin') status = st;
    g.restore();
  }
  return status;
}

// point on a quadratic bezier
export function _qp(x0, y0, cx, cy, x1, y1, u) {
  const w = 1 - u;
  return [w * w * x0 + 2 * w * u * cx + u * u * x1,
          w * w * y0 + 2 * w * u * cy + u * u * y1];
}

function paintConifer(g, S, H, rgb, v, larch) {
  const sv = v * 131;                    // variant seed
  const cx = S / 2;
  const px = S / 128;                    // stroke scale with resolution
  // trunk: tapering, larch shows more of it through the airy crown
  g.fillStyle = larch ? '#5c4126' : '#4a3320';
  const tw = (larch ? 4.5 : 6) * px;
  g.beginPath();
  g.moveTo(cx - tw / 2, H);
  g.lineTo(cx + tw / 2, H);
  g.lineTo(cx + px, H * 0.05);
  g.lineTo(cx - px, H * 0.05);
  g.closePath();
  g.fill();
  const y0 = H * 0.05;
  const yb = H - H * (larch ? 0.16 : 0.09);
  const wMax = S * (larch ? 0.34 : 0.44);
  // solid core (narrow, behind the branches) so the crown never mips to
  // holes — the needle fringe owns the silhouette edge. Larch stays airy.
  if (!larch) {
    g.fillStyle = rgb(0.45);
    const nc = 9;
    for (let c = 0; c < nc; c++) {
      const ft = c / (nc - 1);
      const y = y0 + S * 0.12 + ft * (yb - y0 - S * 0.16);
      const w = (0.08 + 0.92 * Math.pow(ft, 0.85)) * wMax * 0.55;
      g.beginPath();
      g.ellipse(cx, y, w, (yb - y0) / nc * 0.75, 0, 0, Math.PI * 2);
      g.fill();
    }
  }
  // branch whorls, top to bottom; each branch is a drooping stroke fringed
  // with short NEEDLE strokes — this is what reads as "needles not leaves"
  const nT = larch ? 10 : 12;
  for (let t = 0; t < nT; t++) {
    const ft = t / (nT - 1);
    const y = y0 + S * 0.06 + ft * (yb - y0 - S * 0.06);
    const halfW = (0.08 + 0.92 * Math.pow(ft, 0.85)) * wMax
      * (0.9 + _h01(sv + t, 3) * 0.2);
    // larch: upper branches sweep slightly up, lower ones droop; spruce
    // branches hang throughout
    const droop = larch ? (ft < 0.35 ? -0.12 : 0.30 + ft * 0.25)
      : 0.28 + ft * 0.18;
    for (const dir of [-1, 1]) {
      const bx = cx + dir * halfW, by = y + halfW * droop;
      const mx = cx + dir * halfW * 0.45, my = y + halfW * droop * 0.15;
      g.strokeStyle = rgb(0.42 + ft * 0.1);
      g.lineWidth = (larch ? 1.0 : 1.4) * px;
      g.beginPath();
      g.moveTo(cx, y - px);
      g.quadraticCurveTo(mx, my, bx, by);
      g.stroke();
      // needle fringe along the branch
      const nn = Math.max(3, Math.round(halfW / (S * 0.024)));
      for (let j = 0; j <= nn; j++) {
        const u = 0.12 + (j / nn) * 0.88;
        const [nx, ny] = _qp(cx, y - px, mx, my, bx, by, u);
        const hh = _h01(sv + t * 37 + j, dir + 5);
        const len = S * (larch ? 0.018 : 0.024) * (0.7 + hh * 0.8);
        const ang = Math.atan2(1, dir * 0.45) + (hh - 0.5) * 0.9;
        const k = (larch ? 0.7 : 0.52) + hh * (larch ? 0.55 : 0.45)
          + ft * 0.12;
        g.strokeStyle = rgb(k);
        g.lineWidth = (larch ? 0.9 : 1.1) * px;
        g.beginPath();
        g.moveTo(nx, ny);
        g.lineTo(nx + Math.cos(ang) * len * (hh > 0.5 ? 1 : -1),
                 ny + Math.sin(ang) * len);
        g.stroke();
      }
      // sun-side highlight needles (upper edge, right side)
      if (dir > 0) {
        for (let j = 0; j < 4; j++) {
          const u = 0.25 + j * 0.18;
          const [nx, ny] = _qp(cx, y - px, mx, my, bx, by, u);
          g.strokeStyle = rgb(larch ? 1.5 : 1.35);
          g.lineWidth = 0.9 * px;
          g.beginPath();
          g.moveTo(nx, ny - px);
          g.lineTo(nx + S * 0.014, ny - S * 0.02);
          g.stroke();
        }
      }
    }
  }
  // leader: small vertical spike with a few tiny needles
  g.strokeStyle = rgb(0.5);
  g.lineWidth = 1.2 * px;
  g.beginPath();
  g.moveTo(cx, y0 + S * 0.09);
  g.lineTo(cx, y0 - S * 0.015);
  g.stroke();
  for (let j = 0; j < 5; j++) {
    const yy = y0 + S * 0.014 + j * S * 0.016;
    const dd = (j % 2 ? 1 : -1);
    g.strokeStyle = rgb(0.6 + _h01(sv, j) * 0.5);
    g.lineWidth = 0.9 * px;
    g.beginPath();
    g.moveTo(cx, yy);
    g.lineTo(cx + dd * S * 0.02, yy + S * 0.012);
    g.stroke();
  }
}

function paintBroadleaf(g, S, H, rgb, v) {
  const sv = v * 131;
  const cx = S / 2;
  const px = S / 128;
  // forked trunk
  g.strokeStyle = '#4d3a24';
  g.lineWidth = 6 * px;
  g.beginPath();
  g.moveTo(cx, H);
  g.lineTo(cx, H * 0.56);
  g.stroke();
  g.lineWidth = 3.4 * px;
  for (const dir of [-1, 1]) {
    g.beginPath();
    g.moveTo(cx, H * 0.60);
    g.quadraticCurveTo(cx + dir * S * 0.06, H * 0.5, cx + dir * S * 0.14, H * 0.40);
    g.stroke();
  }
  // lobed crown: dark mass first, then mid blobs, then sun-side highlights —
  // deliberately LEAFY (round blobs), the opposite grammar of the needle forms
  const cyc = H * 0.30, rx = S * 0.30, ry = H * 0.135;
  const blob = (bx, by, r, k) => {
    g.fillStyle = rgb(k);
    g.beginPath();
    g.arc(bx, by, r, 0, Math.PI * 2);
    g.fill();
  };
  for (let b = 0; b < 10; b++) {
    const a = (b / 10) * Math.PI * 2;
    blob(cx + Math.cos(a) * rx * (0.8 + _h01(sv + b, 1) * 0.4),
         cyc + Math.sin(a) * ry * (0.8 + _h01(sv + b, 2) * 0.4),
         S * (0.12 + _h01(sv + b, 3) * 0.07), 0.5);
  }
  blob(cx, cyc, S * 0.22, 0.55);
  for (let b = 0; b < 8; b++) {
    const a = (b / 8) * Math.PI * 2 + 0.4;
    blob(cx + Math.cos(a) * rx * 0.6 * (0.7 + _h01(sv + b, 4) * 0.5),
         cyc + Math.sin(a) * ry * 0.55 * (0.7 + _h01(sv + b, 5) * 0.5),
         S * (0.09 + _h01(sv + b, 6) * 0.06), 0.85 + _h01(sv + b, 7) * 0.2);
  }
  for (let b = 0; b < 5; b++) {
    blob(cx + S * (0.04 + _h01(sv + b, 8) * 0.18),
         cyc - ry * (0.3 + _h01(sv + b, 9) * 0.5),
         S * (0.055 + _h01(sv + b, 10) * 0.04), 1.35);
  }
  // leaf speckle on the crown edge
  for (let b = 0; b < 26; b++) {
    const a = _h01(sv + b, 11) * Math.PI * 2;
    const rr = 0.95 + _h01(sv + b, 12) * 0.25;
    g.fillStyle = rgb(0.65 + _h01(sv + b, 13) * 0.75);
    g.beginPath();
    g.arc(cx + Math.cos(a) * rx * rr, cyc + Math.sin(a) * ry * rr,
          (1 + _h01(sv + b, 14)) * px, 0, Math.PI * 2);
    g.fill();
  }
}

// ---------------------------------------------------------------- palm trees
function paintPalm(g, S, H, rgb, v) {
  const sv = v * 131;
  const cx = S / 2;
  const px = S / 128;
  const topY = H * 0.30;
  const curveX = (_h01(sv, 0) - 0.5) * S * 0.06;
  g.strokeStyle = '#8B7355';
  g.lineWidth = 4.5 * px;
  g.beginPath();
  g.moveTo(cx, H);
  g.quadraticCurveTo(cx + curveX, H * 0.55, cx + curveX * 0.6, topY);
  g.stroke();
  g.strokeStyle = '#7a6548';
  g.lineWidth = 3 * px;
  g.beginPath();
  g.moveTo(cx, H);
  g.quadraticCurveTo(cx + curveX, H * 0.55, cx + curveX * 0.6, topY);
  g.stroke();
  for (let r = 0; r < 4; r++) {
    const ry = H * 0.95 - r * H * 0.15;
    g.strokeStyle = r % 2 ? '#6d5a3e' : '#978264';
    g.lineWidth = 1.2 * px;
    g.beginPath();
    g.moveTo(cx - 3 * px + curveX * (1 - (H - ry) / H), ry);
    g.lineTo(cx + 3 * px + curveX * (1 - (H - ry) / H), ry);
    g.stroke();
  }
  const nFronds = 7 + (sv % 3);
  const crownX = cx + curveX * 0.6;
  for (let f = 0; f < nFronds; f++) {
    const angle = (f / nFronds) * Math.PI * 2 + _h01(sv + f, 1) * 0.4;
    const dir = Math.cos(angle);
    const len = S * (0.32 + _h01(sv + f, 2) * 0.12);
    const droopEnd = topY + len * 0.55;
    const tipX = crownX + dir * len;
    const midX = crownX + dir * len * 0.5;
    const midY = topY - len * 0.12;
    const k = dir > 0 ? 1.1 : 0.7;
    g.strokeStyle = rgb(k);
    g.lineWidth = 1.6 * px;
    g.beginPath();
    g.moveTo(crownX, topY);
    g.quadraticCurveTo(midX, midY, tipX, droopEnd);
    g.stroke();
    const nLeaf = Math.max(4, Math.round(len / (S * 0.05)));
    for (let j = 1; j <= nLeaf; j++) {
      const u = j / (nLeaf + 1);
      const [lx, ly] = _qp(crownX, topY, midX, midY, tipX, droopEnd, u);
      const leafLen = S * 0.04 * (1 - u * 0.5);
      const leafK = k * (0.8 + _h01(sv + f * 17 + j, 3) * 0.4);
      g.strokeStyle = rgb(leafK);
      g.lineWidth = 1.0 * px;
      for (const side of [-1, 1]) {
        const la = Math.atan2(droopEnd - topY, tipX - crownX) + side * 1.2;
        g.beginPath();
        g.moveTo(lx, ly);
        g.lineTo(lx + Math.cos(la) * leafLen, ly + Math.sin(la) * leafLen);
        g.stroke();
      }
    }
  }
}

// ------------------------------------------------------------- columnar trees
function paintColumnar(g, S, H, rgb, v) {
  const sv = v * 131;
  const cx = S / 2;
  const px = S / 128;
  g.fillStyle = '#4a3320';
  const tw = 3 * px;
  g.fillRect(cx - tw / 2, H * 0.85, tw, H * 0.15);
  const wMax = S * 0.14;
  const y0 = H * 0.02, yb = H * 0.88;
  g.fillStyle = rgb(0.4);
  g.beginPath();
  g.ellipse(cx, (y0 + yb) / 2, wMax * 0.5, (yb - y0) / 2, 0, 0, Math.PI * 2);
  g.fill();
  const nT = 18;
  for (let t = 0; t < nT; t++) {
    const ft = t / (nT - 1);
    const y = y0 + S * 0.04 + ft * (yb - y0 - S * 0.04);
    const hw = (Math.sin(ft * Math.PI) * 0.85 + 0.15) * wMax
      * (0.85 + _h01(sv + t, 1) * 0.3);
    for (const dir of [-1, 1]) {
      const len = hw * (0.6 + _h01(sv + t * 13 + dir, 2) * 0.4);
      const ang = dir * 0.3 + (dir > 0 ? -0.8 : 0.8);
      const k = 0.5 + ft * 0.15 + _h01(sv + t, 3) * 0.3
        + (dir > 0 ? 0.15 : 0);
      g.strokeStyle = rgb(k);
      g.lineWidth = 1.3 * px;
      g.beginPath();
      g.moveTo(cx, y);
      g.lineTo(cx + Math.cos(ang) * len * dir, y - Math.sin(0.6) * len * 0.3);
      g.stroke();
      for (let j = 0; j < 3; j++) {
        const u = 0.3 + j * 0.25;
        const nx = cx + Math.cos(ang) * len * dir * u;
        const ny = y - Math.sin(0.6) * len * 0.3 * u;
        const nk = k * (0.85 + _h01(sv + t * 7 + j, 4) * 0.3);
        g.strokeStyle = rgb(nk);
        g.lineWidth = 0.9 * px;
        g.beginPath();
        g.moveTo(nx, ny);
        g.lineTo(nx + dir * S * 0.012, ny - S * 0.015);
        g.stroke();
      }
    }
  }
  g.fillStyle = rgb(1.3);
  g.beginPath();
  g.moveTo(cx - wMax * 0.15, y0 + S * 0.06);
  g.lineTo(cx, y0 - S * 0.01);
  g.lineTo(cx + wMax * 0.15, y0 + S * 0.06);
  g.closePath();
  g.fill();
}

// ----------------------------------------------------------- spreading trees
function paintSpreading(g, S, H, rgb, v) {
  const sv = v * 131;
  const cx = S / 2;
  const px = S / 128;
  g.strokeStyle = '#4d3a24';
  g.lineWidth = 8 * px;
  g.beginPath();
  g.moveTo(cx, H);
  g.lineTo(cx, H * 0.55);
  g.stroke();
  g.lineWidth = 4 * px;
  const nBranch = 2 + (sv % 2);
  for (let b = 0; b < nBranch; b++) {
    const dir = b === 0 ? -1 : b === 1 ? 1 : (_h01(sv, b) - 0.5) * 0.4;
    const bx = cx + dir * S * (0.18 + _h01(sv + b, 1) * 0.08);
    g.strokeStyle = '#4d3a24';
    g.beginPath();
    g.moveTo(cx, H * 0.58);
    g.quadraticCurveTo(cx + dir * S * 0.06, H * 0.48, bx, H * 0.38);
    g.stroke();
  }
  const cyc = H * 0.28, rx = S * 0.42, ry = H * 0.12;
  const blob = (bx, by, r, k) => {
    g.fillStyle = rgb(k);
    g.beginPath();
    g.arc(bx, by, r, 0, Math.PI * 2);
    g.fill();
  };
  for (let b = 0; b < 14; b++) {
    const a = (b / 14) * Math.PI * 2;
    blob(cx + Math.cos(a) * rx * (0.7 + _h01(sv + b, 2) * 0.5),
         cyc + Math.sin(a) * ry * (0.7 + _h01(sv + b, 3) * 0.5),
         S * (0.10 + _h01(sv + b, 4) * 0.06), 0.45);
  }
  blob(cx, cyc, S * 0.26, 0.50);
  for (let b = 0; b < 12; b++) {
    const a = (b / 12) * Math.PI * 2 + 0.3;
    blob(cx + Math.cos(a) * rx * 0.55 * (0.6 + _h01(sv + b, 5) * 0.6),
         cyc + Math.sin(a) * ry * 0.5 * (0.6 + _h01(sv + b, 6) * 0.6),
         S * (0.08 + _h01(sv + b, 7) * 0.05), 0.75 + _h01(sv + b, 8) * 0.25);
  }
  for (let b = 0; b < 8; b++) {
    blob(cx + S * (0.05 + _h01(sv + b, 9) * 0.22) * (b % 2 ? 1 : -1),
         cyc - ry * (0.2 + _h01(sv + b, 10) * 0.6),
         S * (0.05 + _h01(sv + b, 11) * 0.04), 1.25);
  }
  for (let b = 0; b < 30; b++) {
    const a = _h01(sv + b, 12) * Math.PI * 2;
    const rr = 0.85 + _h01(sv + b, 13) * 0.35;
    g.fillStyle = rgb(0.55 + _h01(sv + b, 14) * 0.7);
    g.beginPath();
    g.arc(cx + Math.cos(a) * rx * rr, cyc + Math.sin(a) * ry * rr,
          (1 + _h01(sv + b, 15)) * px, 0, Math.PI * 2);
    g.fill();
  }
}

// ------------------------------------------------------------- umbrella trees
function paintUmbrella(g, S, H, rgb, v) {
  const sv = v * 131;
  const cx = S / 2;
  const px = S / 128;
  g.fillStyle = '#5a4430';
  const tw = 5 * px;
  g.beginPath();
  g.moveTo(cx - tw / 2, H);
  g.lineTo(cx + tw / 2, H);
  g.lineTo(cx + 1.5 * px, H * 0.38);
  g.lineTo(cx - 1.5 * px, H * 0.38);
  g.closePath();
  g.fill();
  const crownTop = H * 0.08;
  const crownBot = H * 0.38;
  const crownCy = (crownTop + crownBot) / 2;
  const crownRx = S * 0.36;
  const crownRy = (crownBot - crownTop) / 2;
  g.fillStyle = rgb(0.42);
  g.beginPath();
  g.ellipse(cx, crownCy, crownRx, crownRy, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = rgb(0.55);
  g.beginPath();
  g.ellipse(cx, crownCy - crownRy * 0.15, crownRx * 0.85, crownRy * 0.75, 0, 0, Math.PI * 2);
  g.fill();
  const nEdge = 24;
  for (let e = 0; e < nEdge; e++) {
    const a = (e / nEdge) * Math.PI * 2;
    const ex = cx + Math.cos(a) * crownRx * (0.85 + _h01(sv + e, 1) * 0.25);
    const ey = crownCy + Math.sin(a) * crownRy * (0.85 + _h01(sv + e, 2) * 0.25);
    const k = 0.5 + _h01(sv + e, 3) * 0.5 + (Math.cos(a) > 0 ? 0.15 : 0);
    g.strokeStyle = rgb(k);
    g.lineWidth = 1.1 * px;
    const nLen = S * 0.02 * (0.7 + _h01(sv + e, 4) * 0.6);
    const nAng = a + (_h01(sv + e, 5) - 0.5) * 1.2;
    g.beginPath();
    g.moveTo(ex, ey);
    g.lineTo(ex + Math.cos(nAng) * nLen, ey + Math.sin(nAng) * nLen);
    g.stroke();
  }
  for (let b = 0; b < 6; b++) {
    const bx = cx + (_h01(sv + b, 6) - 0.5) * crownRx * 1.2;
    const by = crownCy + (_h01(sv + b, 7) - 0.5) * crownRy * 0.8;
    g.fillStyle = rgb(1.2 + _h01(sv + b, 8) * 0.3);
    g.beginPath();
    g.arc(bx, by, S * (0.04 + _h01(sv + b, 9) * 0.03), 0, Math.PI * 2);
    g.fill();
  }
}

// --------------------------------------------------------------- birch trees
function paintBirch(g, S, H, rgb, v) {
  const sv = v * 131;
  const cx = S / 2;
  const px = S / 128;
  g.strokeStyle = '#d4cdb8';
  g.lineWidth = 3.5 * px;
  g.beginPath();
  g.moveTo(cx, H);
  g.lineTo(cx, H * 0.20);
  g.stroke();
  g.strokeStyle = '#c4bda8';
  g.lineWidth = 2 * px;
  g.beginPath();
  g.moveTo(cx, H);
  g.lineTo(cx, H * 0.20);
  g.stroke();
  for (let m = 0; m < 8; m++) {
    const my = H * 0.30 + m * H * 0.08;
    if (my > H * 0.92) break;
    g.fillStyle = `rgba(60,50,40,${0.3 + _h01(sv + m, 0) * 0.4})`;
    const mw = (1 + _h01(sv + m, 1)) * 1.5 * px;
    g.fillRect(cx - mw, my, mw * 2, 1.2 * px);
  }
  const nBr = 5 + (sv % 3);
  for (let b = 0; b < nBr; b++) {
    const dir = b % 2 ? 1 : -1;
    const by0 = H * (0.22 + b * 0.08 + _h01(sv + b, 2) * 0.04);
    if (by0 > H * 0.75) continue;
    const bLen = S * (0.15 + _h01(sv + b, 3) * 0.12);
    const tipX = cx + dir * bLen;
    const tipY = by0 + bLen * (0.5 + _h01(sv + b, 4) * 0.3);
    const midX = cx + dir * bLen * 0.4;
    const midY = by0 - bLen * 0.05;
    g.strokeStyle = '#8a7a64';
    g.lineWidth = 1.2 * px;
    g.beginPath();
    g.moveTo(cx, by0);
    g.quadraticCurveTo(midX, midY, tipX, tipY);
    g.stroke();
    const nLeaves = Math.max(5, Math.round(bLen / (S * 0.02)));
    for (let j = 0; j < nLeaves; j++) {
      const u = 0.15 + (j / nLeaves) * 0.85;
      const [lx, ly] = _qp(cx, by0, midX, midY, tipX, tipY, u);
      const jx = (_h01(sv + b * 19 + j, 5) - 0.5) * S * 0.04;
      const jy = (_h01(sv + b * 19 + j, 6) - 0.5) * S * 0.03;
      const k = 0.6 + _h01(sv + b * 19 + j, 7) * 0.7
        + (dir > 0 ? 0.15 : 0);
      g.fillStyle = rgb(k);
      g.beginPath();
      g.arc(lx + jx, ly + jy, (0.8 + _h01(sv + b * 19 + j, 8) * 0.8) * px,
            0, Math.PI * 2);
      g.fill();
    }
  }
  for (let d = 0; d < 12; d++) {
    const dx = cx + (_h01(sv + d, 9) - 0.5) * S * 0.35;
    const dy = H * (0.12 + _h01(sv + d, 10) * 0.35);
    const k = 0.7 + _h01(sv + d, 11) * 0.6;
    g.fillStyle = rgb(k);
    g.beginPath();
    g.arc(dx, dy, (0.6 + _h01(sv + d, 12) * 0.8) * px, 0, Math.PI * 2);
    g.fill();
  }
}

// ----------------------------------------------------------- form registry
export const FORM_PAINTERS = {
  conifer:   (g, S, H, rgb, v) => paintConifer(g, S, H, rgb, v, false),
  larch:     (g, S, H, rgb, v) => paintConifer(g, S, H, rgb, v, true),
  broadleaf: paintBroadleaf,
  palm: paintPalm, columnar: paintColumnar, spreading: paintSpreading,
  umbrella: paintUmbrella, birch: paintBirch,
};

const _customPainterCache = new Map();
// keyed by name AND code: one page (the dashboard) previews several worlds,
// and two worlds may give the same custom form name different paint code
function _getCustomPainter(form, code) {
  const key = form + '\0' + code;
  if (_customPainterCache.has(key)) return _customPainterCache.get(key);
  try {
    const fn = new Function('g', 'S', 'H', 'rgb', 'v', '_h01', '_qp', code);
    _customPainterCache.set(key, fn);
    return fn;
  } catch { _customPainterCache.set(key, null); return null; }
}

// returns how the form was painted: 'builtin' | 'custom' | 'fallback'
// (a custom form whose code does not compile or throws is drawn as
// broadleaf — the dashboard preview says so; the game ignores the answer)
export function paintForm(form, g, S, H, rgb, v, vegForms) {
  const builtin = FORM_PAINTERS[form];
  if (builtin) { builtin(g, S, H, rgb, v); return 'builtin'; }
  const def = vegForms && vegForms[form];
  if (def && def.paint) {
    const fn = _getCustomPainter(form, def.paint);
    if (fn) { try { fn(g, S, H, rgb, v, _h01, _qp); return 'custom'; } catch { /* fall through */ } }
  }
  paintBroadleaf(g, S, H, rgb, v);
  return 'fallback';
}

// The species slots exactly as Forest._setupSpecies resolves them: ≤ 4, an
// unknown form (no built-in painter, no custom paint code) becomes conifer, a
// bad colour takes the form's default, shares renormalise to 1.
export function resolveSpecies(veg) {
  const vegForms = (veg && veg.forms) || {};
  const sp = ((veg && veg.species) || []).slice(0, 4).map((s) => ({
    form: FORM_CFG[s.form] ? s.form
      : (vegForms[s.form] && vegForms[s.form].paint) ? s.form : 'conifer',
    color: HEXRE.test(s.color || '') ? s.color : (FORM_CFG[s.form] || _DCFG).color,
    share: Number.isFinite(s.share) && s.share > 0 ? s.share : 0,
  }));
  const tot = sp.reduce((a, s) => a + s.share, 0);
  if (tot <= 0) sp.forEach((s) => { s.share = 1 / sp.length; });
  else sp.forEach((s) => { s.share /= tot; });
  return sp;
}

// canopy luminance of a species colour — the ONE measure the brightness LUT
// and the treeline ramp (trees without an ortho reading) rank species by
export function speciesLum(hex) {
  const c = parseInt(hex.slice(1), 16);
  return ((c >> 16) & 255) * 0.35 + ((c >> 8) & 255) * 0.5 + (c & 255) * 0.15;
}

// appearance byte (canopy brightness in the bake's ortho, 0..254) → species
// index, share-weighted: the darkest fraction of stands gets the darkest
// species. `hist` = per-byte tree counts (index 255 = no ortho, ignored).
// Returns null when there is nothing to rank.
export function speciesLut(hist, species) {
  if (!hist || !species || !species.length) return null;
  let tot = 0;
  for (let b = 0; b < 255; b++) tot += hist[b] || 0;
  if (!tot) return null;
  const lum = species.map((s) => speciesLum(s.color));
  const order = species.map((_, i) => i).sort((a, b) => lum[a] - lum[b]);
  const cum = [];
  let c = 0;
  for (const oi of order) { c += species[oi].share; cum.push(c); }
  const lut = new Uint8Array(255);
  let acc = 0, k = 0;
  for (let b = 0; b < 255; b++) {
    acc += hist[b] || 0;
    while (k < order.length - 1 && acc > cum[k] * tot) k++;
    lut[b] = order[k];
  }
  return lut;
}

// ------------------------------------------------ bushes, flowers, tufts
export const BUSH_VAR = 2;   // atlas columns: 0 = leafy shrub, 1 = needle shrub
// a bush quad's world width / height: the square S x S cell is drawn 1.6x as
// wide as tall (shrubs.js / grass.js instance scale) — the painters are
// authored for that stretch; bushSpriteCell undoes it for the owner's PNG
export const BUSH_ASPECT = 1.6;

export function paintBushCanvas(cv, colorHex, bloomHex, S = 64) {
  // S: 64 for the ankle-height grass garnish; shrubs.js passes 128 because
  // its measured hedges stand 0.5-3 m and get walked right up to. The
  // painting logic is authored in 64-px coordinates — the context transform
  // scales it, so every size renders the same silhouettes, only crisper.
  cv.width = S * BUSH_VAR; cv.height = S;
  const g = cv.getContext('2d');
  g.clearRect(0, 0, cv.width, S);
  g.scale(S / 64, S / 64);
  const cr = parseInt(colorHex.slice(1, 3), 16);
  const cg = parseInt(colorHex.slice(3, 5), 16);
  const cb = parseInt(colorHex.slice(5, 7), 16);
  const rgb = (k) => `rgb(${Math.min(255, cr * k) | 0},${Math.min(255, cg * k) | 0},${Math.min(255, cb * k) | 0})`;
  // column 0 — leafy shrub: overlapping leaf blobs in the profile's foliage
  // color, sun-lit toward the top, optional bloom sprinkle (the region's
  // shrubs.bloom, e.g. alpenrose pink; omitted when its shrubs do not flower)
  for (let b = 0; b < 16; b++) {
    const bx = 10 + ((b * 37) % 44);
    const by = 64 - 6 - ((b * 23) % 34);
    const r = 6 + (b * 13) % 8;
    const k = 0.55 + ((b * 31) % 55) / 80 + (by < 64 * 0.55 ? 0.3 : 0);
    g.fillStyle = rgb(k);
    g.beginPath();
    g.arc(bx, by, r, 0, Math.PI * 2);
    g.fill();
  }
  if (bloomHex) {
    g.fillStyle = bloomHex;
    for (let b = 0; b < 9; b++) {
      const bx = 12 + ((b * 29) % 40);
      const by = 64 - 12 - ((b * 41) % 30);
      g.beginPath();
      g.arc(bx, by, 1.6 + (b * 7) % 2, 0, Math.PI * 2);
      g.fill();
    }
  }
  // column 1 — NEEDLE shrub (dwarf pine): clumps of short needle strokes
  // radiating from low branch points, darker than the canopy
  g.save();
  g.translate(64, 0);   // columns are 64 units apart in authored space
  const knots = [[14, 52], [30, 46], [46, 53], [22, 38], [40, 36], [32, 26], [18, 27], [47, 41]];
  for (let ki = 0; ki < knots.length; ki++) {
    const [kx, ky] = knots[ki];
    for (let j = 0; j < 12; j++) {
      const a = -Math.PI * 0.95 + (j / 11) * Math.PI * 0.9 + ((ki * 13 + j * 7) % 10) / 30;
      const len = 6 + ((ki * 17 + j * 11) % 6);
      const k = 0.5 + ((ki * 31 + j * 19) % 50) / 70;
      g.strokeStyle = rgb(k * 0.85);
      g.lineWidth = 1.3;
      g.beginPath();
      g.moveTo(kx, ky);
      g.lineTo(kx + Math.cos(a) * len, ky + Math.sin(a) * len);
      g.stroke();
    }
  }
  g.restore();
}

// ------------------------------------------------ the owner's sprite PNGs
// One atlas cell (x, y, w, h) drawn from the owner's PNG: CONTAIN-FIT (aspect
// kept, scaled to the cell's limiting side), the base at the BOTTOM CENTRE;
// flat and unlit — the material's alphaTest (TREE_ALPHA / BUSH_ALPHA) cuts it
// like a painted cell. Keep in step with public/world-config.js vegSpriteCell
// (the dashboard preview draws the same fit).
export function spriteCell(g, img, x, y, w, h) {
  const k = Math.min(w / img.width, h / img.height);
  const dw = img.width * k, dh = img.height * k;
  g.drawImage(img, x + (w - dw) / 2, y + h - dh, dw, dh);
}

// a species atlas (paintSpeciesCanvas: NVAR cells) with the owner's sprite in
// all NVAR cells: contain-fit, the trunk base at the bottom centre (spriteCell
// — in step with world-config.js vegSpriteCell, the dashboard preview). The
// dashboard's canvas (vegmat.js spriteAtlas) and the pool's worker canvas
// (pool-veg.js veg.sprite) run this one function.
export function treeSpriteAtlas(cv, img) {
  const g = cv.getContext('2d');
  const S = cv.width / NVAR, H = cv.height;
  g.clearRect(0, 0, cv.width, H);
  g.imageSmoothingQuality = 'high';
  for (let v = 0; v < NVAR; v++) spriteCell(g, img, v * S, 0, S, H);
}

// a bush atlas (paintBushCanvas: BUSH_VAR square S x S cells) with cell v —
// 0 = leafy (shrubs.sprite_leafy), 1 = needle (shrubs.sprite_needle) —
// replaced by the owner's PNG, fitted in WORLD aspect: contain-fit into the
// quad's BUSH_ASPECT·S x S box, base at the bottom centre, then x squeezed by
// 1/BUSH_ASPECT into the cell — the instance scale stretches it back, so the
// headset shows the PNG in its own proportions (h/w 1/BUSH_ASPECT fills the
// quad). Keep in step with public/world-config.js: its preview contain-fits
// the PNG into a BUSH_ASPECT : 1 box, bottom centre (world shape, no squeeze).
// The painter leaves its 64-px scale on the context: reset it first.
export function bushSpriteCell(cv, img, v) {
  const g = cv.getContext('2d');
  const S = cv.height;
  const k = Math.min(BUSH_ASPECT * S / img.width, S / img.height);
  const dw = img.width * k / BUSH_ASPECT, dh = img.height * k;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(v * S, 0, S, S);
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, v * S + (S - dw) / 2, S - dh, dw, dh);
}

export function paintFlowerCanvas(cv) {
  const S = 64;
  cv.width = S; cv.height = S;
  const g = cv.getContext('2d');
  g.clearRect(0, 0, S, S);
  // a small mixed bouquet: green stems, colored heads (alpine meadow mix)
  const heads = ['#ecdf6a', '#f2f2f4', '#b287d8', '#e08bb2', '#ecdf6a', '#f2f2f4'];
  for (let b = 0; b < 6; b++) {
    const bx = 8 + b * 9 + (b * 41 % 5) - 2;
    const lean = ((b * 67) % 100) / 100 * 14 - 7;
    const h = S * (0.5 + ((b * 23) % 100) / 100 * 0.4);
    g.strokeStyle = 'rgb(58,96,44)';
    g.lineWidth = 1.6;
    g.beginPath();
    g.moveTo(bx, S);
    g.quadraticCurveTo(bx + lean * 0.5, S - h * 0.6, bx + lean, S - h);
    g.stroke();
    g.fillStyle = heads[b];
    const r = 2.6 + (b * 31 % 3);
    g.beginPath(); g.arc(bx + lean, S - h, r, 0, Math.PI * 2); g.fill();
    g.fillStyle = 'rgba(120,90,20,0.85)';
    g.beginPath(); g.arc(bx + lean, S - h, r * 0.35, 0, Math.PI * 2); g.fill();
  }
}

// grass tufts: TUFT_VAR atlas columns of S px, one shape each (the colour is
// the instance's — the world's grass palette — so the painting stays in one
// green family and differs in SHAPE and light only): 0 = meadow grass,
// 1 = short wiry turf, 2 = tall sedge / reed, 3 = meadow grass in seed.
// Every blade is a tapered filled stroke, dark at the root (it stands IN the
// ground) to a sunlit tip. nvar 1 (the dashboard preview): column 0 alone.
export const TUFT_VAR = 4;
export function paintTuftCanvas(cv, S = 64, nvar = 1) {
  cv.width = S * nvar; cv.height = S;
  const g = cv.getContext('2d');
  g.clearRect(0, 0, cv.width, S);
  const k = S / 64;                      // authored in 64-px units
  // [blades, height lo..hi (x S), width (px@64), lean (px@64), tip lift]
  const SHAPE = [
    [16, 0.5, 0.95, 2.4, 13, 1.0],
    [24, 0.3, 0.55, 1.6, 9, 1.12],
    [9, 0.75, 1.0, 3.6, 8, 0.95],
    [13, 0.45, 0.85, 2.2, 13, 1.0],
  ];
  let seed = 7;
  const rnd = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
  for (let v = 0; v < nvar; v++) {
    const [nb, h0, h1, w0, lean0, lift] = SHAPE[v];
    const ox = v * S;
    g.save();
    g.beginPath(); g.rect(ox, 0, S, S); g.clip();
    for (let b = 0; b < nb; b++) {
      const bx = ox + (10 + rnd() * 44) * k;
      const lean = (rnd() * 2 - 1) * lean0 * k;
      const h = S * (h0 + rnd() * (h1 - h0)) * (0.92 + 0.08 * Math.min(1, b / 3));
      const w = w0 * k * (0.7 + rnd() * 0.6);
      const tx = Math.min(ox + S - 3 * k, Math.max(ox + 3 * k, bx + lean));
      const ty = S - h;
      const lum = 0.85 + rnd() * 0.3;
      const gr = g.createLinearGradient(0, S, 0, ty);
      gr.addColorStop(0, `rgb(${18 * lum | 0},${46 * lum | 0},${14 * lum | 0})`);
      gr.addColorStop(0.45, `rgb(${50 * lum | 0},${120 * lum | 0},${36 * lum | 0})`);
      gr.addColorStop(1, `rgb(${Math.min(255, 96 * lum * lift) | 0},${Math.min(255, 176 * lum * lift) | 0},${Math.min(255, 66 * lum * lift) | 0})`);
      g.fillStyle = gr;
      const cx = bx + lean * 0.35, cy = S - h * 0.6;
      g.beginPath();
      g.moveTo(bx - w / 2, S);
      g.quadraticCurveTo(cx - w * 0.3, cy, tx, ty);
      g.quadraticCurveTo(cx + w * 0.3, cy, bx + w / 2, S);
      g.closePath();
      g.fill();
    }
    if (v === 3) {
      // seed heads: a few bare stems topped with a spikelet panicle
      for (let b = 0; b < 4; b++) {
        const bx = ox + (14 + rnd() * 36) * k;
        const lean = (rnd() * 2 - 1) * 8 * k;
        const h = S * (0.82 + rnd() * 0.16);
        const tx = bx + lean, ty = S - h;
        g.strokeStyle = 'rgb(92,128,58)';
        g.lineWidth = 0.9 * k;
        g.beginPath(); g.moveTo(bx, S); g.quadraticCurveTo(bx + lean * 0.4, S - h * 0.55, tx, ty); g.stroke();
        g.fillStyle = 'rgb(150,160,92)';
        for (let i = 0; i < 6; i++) {
          const t = i / 6;
          g.beginPath();
          g.ellipse(tx - lean * 0.08 * t + (i % 2 ? 1.6 : -1.6) * k, ty + t * 9 * k, 1.1 * k, 2.2 * k, (i % 2 ? 0.5 : -0.5), 0, Math.PI * 2);
          g.fill();
        }
      }
    }
    g.restore();
  }
}

// ---------------------------------------------------------- season page
// The sprite atlas's second page (gs-sprite-snow, APP_FORESTVOL §5), derived
// from the atlas pixels themselves — the painted forms and the owner's PNG
// alike (one rule, on device): R = snow-catch, 1 at an upward-facing
// silhouette edge (the pixel above transparent) falling to 0 over
// SNOW_DEPTH of the atlas height into the crown; G = foliage (what a bare
// tree drops): an opaque, chromatic, green-over-red pixel — wood (r ≥ g) and
// achromatic bark (birch) stay. B 0, A 255. px RGBA (w × h), alpha cut a0.
export const SNOW_DEPTH = 1 / 32;
export function isFoliage(r, g, b) {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  return g > r && mx - mn >= 12;
}
// R8d: a STEP GENERATOR over column ranges (a whole atlas was one ~70-200
// ms task); seasonPage = its drain, the same bytes. vegmat.js setSpritePage
// runs it sliced (decode.js runSteps). R8d2: a step = the columns of ~PAGE_PX
// pixels (32 at h 1024, 16 at h 2048: ~1-2 ms on a desktop), the zeroed
// page's allocation a step of its own (16 MB at 2048²).
const PAGE_PX = 1 << 15;
export function seasonPage(px, w, h, a0 = Math.round(TREE_ALPHA * 255)) {
  const it = seasonPageSteps(px, w, h, a0);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* seasonPageSteps(px, w, h, a0 = Math.round(TREE_ALPHA * 255)) {
  const out = new Uint8ClampedArray(w * h * 4);
  const D = Math.max(2, Math.round(h * SNOW_DEPTH));
  const cols = Math.max(1, Math.floor(PAGE_PX / Math.max(1, h)));
  yield;
  for (let x = 0; x < w; x++) {
    if (x % cols === 0 && x) yield;
    let depth = 0;                                   // above the image: open sky
    for (let y = 0; y < h; y++) {
      const k = (y * w + x) * 4;
      if (px[k + 3] < a0) { depth = 0; out[k + 3] = 255; continue; }
      out[k] = Math.round(255 * Math.max(0, 1 - depth / D));
      out[k + 1] = isFoliage(px[k], px[k + 1], px[k + 2]) ? 255 : 0;
      out[k + 3] = 255;
      depth++;
    }
  }
  return out;
}
